// src/components/Users/UserModal.js
import React, { useState, useEffect } from 'react';
import { X, User, Mail, Lock, Shield } from 'lucide-react';

const UserModal = ({ isOpen, onClose, onSave, user = null, loading = false }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user',
    is_active: true
  });
  const [errors, setErrors] = useState({});

  const isEditMode = !!user;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && user) {
        setFormData({
          username: user.username || '',
          password: '', // Never pre-fill password
          full_name: user.full_name || '',
          email: user.email || '',
          role: user.role || 'user',
          is_active: user.is_active !== undefined ? user.is_active : true
        });
      } else {
        setFormData({
          username: '',
          password: '',
          full_name: '',
          email: '',
          role: 'user',
          is_active: true
        });
      }
      setErrors({});
    }
  }, [isOpen, isEditMode, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required for new users';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const submitData = { ...formData };

    // Don't send empty password in edit mode
    if (isEditMode && !submitData.password) {
      delete submitData.password;
    }

    onSave(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEditMode ? 'Edit User' : 'Add New User'}
          </h2>
          <button
            onClick={onClose}
            className="modal-close"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Username */}
          <div className="form-group">
            <label className="form-label">
              <User size={16} />
              Username *
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={`form-input ${errors.username ? 'error' : ''}`}
              placeholder="Enter username"
              disabled={loading}
              autoComplete="username"
            />
            {errors.username && (
              <span className="error-text">{errors.username}</span>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              Password {isEditMode ? '(leave empty to keep current)' : '*'}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder={isEditMode ? 'Enter new password' : 'Enter password'}
              disabled={loading}
              autoComplete={isEditMode ? "new-password" : "new-password"}
            />
            {errors.password && (
              <span className="error-text">{errors.password}</span>
            )}
          </div>

          {/* Full Name */}
          <div className="form-group">
            <label className="form-label">
              <User size={16} />
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={`form-input ${errors.full_name ? 'error' : ''}`}
              placeholder="Enter full name"
              disabled={loading}
              autoComplete="name"
            />
            {errors.full_name && (
              <span className="error-text">{errors.full_name}</span>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">
              <Mail size={16} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="Enter email (optional)"
              disabled={loading}
              autoComplete="email"
            />
            {errors.email && (
              <span className="error-text">{errors.email}</span>
            )}
          </div>

          {/* Role */}
          <div className="form-group">
            <label className="form-label">
              <Shield size={16} />
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="form-input"
              disabled={loading}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Active Status */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="checkbox-text">User is active</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update User' : 'Create User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;